import type { ClientOrchestrationCommand, ModelSelection } from "@cmmd-forge/contracts";
import { useRef, useState } from "react";
import { scopeThreadRef } from "@cmmd-forge/client-runtime";
import { useComposerDraftStore } from "../../composerDraftStore";
import { runThreadExecutionIntentAction } from "../../lib/runThreadExecutionIntentAction";
import { newCommandId, newMessageId } from "../../lib/utils";
import type { UsageLimitRecoveryProps } from "./UsageLimitRecoveryDialog";

type ContinuationCommand = Extract<
  ClientOrchestrationCommand,
  { readonly type: "thread.turn.start" }
>;

function continuationCommand(
  props: UsageLimitRecoveryProps,
  accountId: string,
): ContinuationCommand {
  return {
    type: "thread.turn.start",
    commandId: newCommandId(),
    threadId: props.threadId,
    message: {
      messageId: newMessageId(),
      role: "user",
      text: "Continue from where you stopped.",
      attachments: [],
    },
    modelSelection: { ...props.modelSelection, providerAccountId: accountId } as ModelSelection,
    runtimeMode: props.runtimeMode ?? "approval-required",
    interactionMode: props.interactionMode ?? "default",
    createdAt: new Date().toISOString(),
  };
}

export function useAccountContinuation(props: UsageLimitRecoveryProps) {
  const setModelSelection = useComposerDraftStore((store) => store.setModelSelection);
  const [state, setState] = useState<"idle" | "switching" | "resumed">("idle");
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const command = useRef<{
    readonly accountId: string;
    readonly value: ContinuationCommand;
  } | null>(null);

  async function resume(accountId: string) {
    if (inFlight.current) return;
    if (command.current && command.current.accountId !== accountId) {
      setError(
        "Check the conversation before switching again. The previous resume could not be confirmed.",
      );
      return;
    }
    inFlight.current = true;
    setState("switching");
    setError(null);
    command.current ??= { accountId, value: continuationCommand(props, accountId) };
    try {
      const continuation = command.current.value;
      await runThreadExecutionIntentAction(
        props.environmentId,
        {
          ...(props.runtimeId ? { runtimeId: props.runtimeId } : {}),
          action: "send",
          projectId: props.projectId,
          targetId: props.threadId,
          dispatchIdentity: continuation.commandId,
        },
        { dispatch: (api) => api.orchestration.dispatchCommand(continuation) },
      );
      setModelSelection(
        scopeThreadRef(props.environmentId, props.threadId),
        continuation.modelSelection,
      );
      setState("resumed");
      props.onResumed?.();
    } catch {
      inFlight.current = false;
      setState("idle");
      setError("Couldn't resume with this account. Try again.");
    }
  }
  return { state, error, resume, lockedAccountId: command.current?.accountId ?? null };
}
